import React from 'react';
import {
    ListButton,
    ShowButton,
    EditButton,
    TextField,
    BooleanField,
    SimpleShowLayout,
    SimpleForm,
    ReferenceInput,
    SelectInput,
    required
} from 'admin-on-rest';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import ListTableContainer from '../components/ListTableContainer';
import List from '../admin/List';
import ShowWithType from '../admin/ShowWithType';
import Create from '../admin/Create';
import EditWithType from '../admin/EditWithType';
import {DependentInput} from 'aor-dependent-input';
import ValidatedProductTextInput from '../components/ValidatedProductTextInput';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ProductUnitForAliasDependentSelectInput from "../components/ProductUnitForAliasDependentSelectInput";
import ViewButtonWithType from '../buttons/ViewButtonWithType';
import EditButtonWithType from '../buttons/EditButtonWithType';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import {CardActions} from 'material-ui/Card';
import Icon from 'material-ui/svg-icons/action/class';
import {RefreshButton} from 'admin-on-rest/lib/mui/button';
import ProductTypesSelectInput from '../components/ProductTypesSelectInput';

export const fuseProductAliasIcon = Icon;

const styles = {
    rowShowField: {minWidth: 150},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
};

const getBasePathUpOne = (basePath) => {
    return basePath
        .split('/')
        .slice(0, -1)
        .join('/');
};

// Create Filter: Product Type + Unit Type
const ListFilter = (props) => (
    <FilterWithSubmit {...props} updateOnChange={true} displayButton={false}>
        <SelectInput
            source="unit_type"
            label="Select Unit Type"
            dependsOn="type"
            alwaysOn
            choices={[
                {id: 'Piece', name: 'Piece'},
                {id: 'Mid', name: 'Mid'},
                {id: 'Case', name: 'Case'},
            ]}
            style={viewStyles.filterInline}
        />
        <ProductTypesSelectInput
            {...props}
            source="type"
            optionValue="name"
            label="Select Product Type"
            validate={[required]}
            allowEmpty
            alwaysOn
            style={{...viewStyles.filterInline, ...viewStyles.filterThird}}
        />
    </FilterWithSubmit>
);

// Set title for List SKUs
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>SKUs for all Product Units</span>;

const ListDescription = () => {
    return (
        <span>
            <img src="img-diagram-Product-Sku-List.png" alt="Diagram of Products - SKUs"
                 style={viewStyles.diagramRight}/>
            <p>Product <strong>SKUs</strong> are created when Product Units need to be identified by a different
                barcode number.</p>
            <p>All Product Units have a barcode, but if a Product Unit has another barcode associated with it,
                click <strong>Create</strong> to add a a new SKU.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <img src="img-diagram-Product-Sku-List.png" alt="Diagram of Products - SKUs"
                 style={viewStyles.diagramRight}/>
            <p>Product <strong>SKUs</strong> are created when Product Units need to be identified by a different
                barcode number.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

// Set columns and behavior for table
const ListColumns = [
    {source: 'sku', label: 'SKU / Barcode', type: 'text'},
    {source: 'product_unit_id', label: 'Product Unit', type: 'text'},
    {source: 'product_name', label: 'Product', type: 'text'},
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: '', label: '', component: <EditButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden},
    {source: 'id', label: '', style: viewStyles.hidden}
];

const ListColumnsViewOnly = [
    {source: 'sku', label: 'SKU / Barcode', type: 'text'},
    {source: 'product_unit_id', label: 'Product Unit', type: 'text'},
    {source: 'product_name', label: 'Product', type: 'text'},
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden},
    {source: 'id', label: '', style: viewStyles.hidden}
];

export const fuseProductAliasList = (props) => (
    <List title={ListTitle} desc={<ListDescription/>}
          listType="table"
          filters={<ListFilter/>}
          requiresFilter={true}
          allowQueryString={false}
          pagination={null}
          {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={10}
            currentSort={{field: 'sku', order: 'ASC'}}
        />
    </List>
);

export const fuseProductAliasListViewOnly = (props) => (
    <List title={ListTitle} desc={<ListDescriptionViewOnly/>}
          listType="table"
          filters={<ListFilter/>}
          requiresFilter={true}
          allowQueryString={false}
          pagination={null}
          {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={10}
            currentSort={{field: 'sku', order: 'ASC'}}
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show SKU
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `SKU #${record.sku}` : ''}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Product <strong>SKUs</strong> cannot be edited. If this SKU's information is incorrect,
                click <strong>Edit</strong> to set this SKU to <em>Inactive</em>, then create a new one.</p>
        </span>
    );
};

// Replace Actions for Show SKU
const ShowActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        {canEdit &&
        <EditButton basePath={basePath} record={data}/>
        }
        <RefreshButton/>
    </CardActions>
);

/*
 * Show SKU Table
 */
export const fuseProductAliasShow = ({canEdit, ...props}) => (
    <ShowWithType {...props} title={<ShowTitle/>} desc={<ShowDescription/>} actions={<ShowActions canEdit={canEdit}/>}>
        <SimpleShowLayout>
            <TextField source="type" label="Product Type"/>
            <hr style={viewStyles.break}/>
            <TextField source="product_id" label="Product ID" style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
            <TextField source="product_name" label="Product Name" style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>
            <hr style={viewStyles.break}/>
            <TextField source="product_unit_id" label="Product Unit Barcode #"/>
            <TextField source="sku" label="SKU / Barcode #"/>
            <hr style={viewStyles.break}/>
            <TextField source="unit_type" label="Unit Type"/>
            <BooleanField source="active" label="Active" style={{...viewStyles.rowShowField, ...styles.rowShowField}} elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </ShowWithType>
);

// Set title for Create SKU
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a SKU for a Product Unit</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Product <strong>SKUs</strong> are created when Product Units need to be identified by a different barcode
                number. All Product Units have a barcode, but if a Product Unit has another barcode associated with it,
                create a SKU by selecting a Product Unit and entering the barcode number.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {active: true};

/*
 * Create SKU form
 */
export const fuseProductAliasCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="list">

            <ReferenceInput
                label="Select a Product Type*"
                source="type"
                reference="ProductTypes"
                perPage={10000}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    options={{
                        clearable: false,
                        labelKey: "name",
                        placeholder: "Select a Product Type*",
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                First select a <strong>Product Type</strong>.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <DependentInput dependsOn="type">
                <ProductUnitForAliasDependentSelectInput
                    {...props}
                    source="product_unit_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="choice_name"
                    label="Select a Product Unit*"
                    validate={[required]}
                    allowEmpty/>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Product Unit</strong> to associate with this SKU.
                    <br/>
                    (If Product Unit is not available, please exit and go
                    to <a href="#/ProductUnits/create" style={viewStyles.fieldLink}>Add Product Unit</a>).
                    <TooltipIcon dataFor="ttUnit"/>
                    <ReactTooltip id="ttUnit" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <p>A <strong>Product Unit</strong> for a Product is a physical item or set sold at your
                            Location(s). Product Units have a barcode, a Unit Type, and a default price.
                        </p>
                        <p>
                            A Product Unit can be a single sellable item (called a <em>Piece</em>) or multiple pieces
                            bundled together (<em>Mid</em>, <em>Case</em>) to be sold.
                        </p>
                    </ReactTooltip>
                </span>
            </DependentInput>

            <DependentInput dependsOn="type">
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    Enter the unique barcode for this <strong>SKU</strong>.
                    <TooltipIcon dataFor="ttId"/>
                    <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        In most cases, a SKU / barcode for
                        a <em>Piece</em>, <em>Mid</em> and <em>Case</em> will have the same number as its Product but
                        with an extra digit.
                        <br/><br/>
                        For example, a Product with Product ID 1234 might have a Product
                        Unit <em>Piece</em> barcode of 12341, a Product Unit <em>Mid</em> of 12342, and a Product
                        Unit <em>Case</em> of 12345.
                    </ReactTooltip>
                </span>
                <ValidatedProductTextInput
                    formName="record-form"
                    source="sku"
                    parentField="type"
                    fieldType="text"
                    label="ID / Barcode*"
                    buttonLabel="Check Availability"
                    successMessage="This ID / Barcode is available."
                    errorDuplicateMessage="A SKU with this Barcode already exists."
                    errorRequiredMessage="A unique ID / Barcode is required."
                    fieldResource="ProductAliases"
                    isRequired={true}
                />
            </DependentInput>

            <SelectInput source="active" label="SKU is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, scanning this SKU barcode will display the selected Product Unit on the
                    POS System.
                    <br/><br/>
                    If <em>Inactive</em>, scanning this SKU barcode will not display any Product Unit on the POS
                    System.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the SKUs List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

        </SimpleForm>
    </Create>
);

// Set title for Edit SKU
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `SKU ${record.id}` : 'SKU'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Product <strong>SKUs</strong> cannot be edited. If this SKU's information is incorrect,
                set it to <em>Inactive</em> to deactivate this SKU, then create a new one.</p>
        </span>
    );
};

// Replace Actions for Edit SKU
const EditActions = ({basePath, data}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <ShowButton basePath={basePath} record={data}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Edit Product SKU form
 */
export const fuseProductAliasEdit = (props) => (
    <EditWithType {...props} title={<EditTitle/>} desc={<EditDescription/>} actions={<EditActions/>}>
        <SimpleForm>
            <span style={viewStyles.fieldHeader}>SKU / Product Type</span>
            <TextField source="type" label="Type" style={{...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight}}/>
            <TextField source="sku" label="SKU / Barcode #" style={{...viewStyles.rowShowField, ...styles.rowShowField}}/>

            <span style={viewStyles.fieldHeader}>Current Product / Product Unit</span>
            <TextField source="product_name" label="Product" style={{...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight}}/>
            <TextField source="current_product_unit_id" label="Product Unit ID"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight}}/>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldHeader}>Change Product Unit</span>
            <DependentInput dependsOn="type">
                <ProductUnitForAliasDependentSelectInput
                    {...props}
                    source="product_unit_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="choice_name"
                    label="Change Product Unit*"
                    validate={[required]}
                    allowEmpty/>
            </DependentInput>
            <span style={viewStyles.fieldDescription}>
                    To change the <strong>Product Unit</strong> associated with this SKU, select a new Product Unit.
                    <TooltipIcon dataFor="ttPId"/>
                    <ReactTooltip id="ttPId" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <p>
                            Selecting a new Product Unit will assign this SKU to a different Product.
                        </p>
                    </ReactTooltip>
                </span>
            <hr style={viewStyles.break}/>

            <SelectInput source="active" label="SKU is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, scanning this SKU barcode will display the selected Product Unit on the
                    POS System.
                    <br/><br/>
                    If <em>Inactive</em>, scanning this SKU barcode will not display any Product Unit on the POS
                    System.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the SKUs List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </EditWithType>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/alias.js